import React, { useEffect, useState } from "react";
import { Select, Input, Space, Divider, Radio, InputNumber } from "antd";
import axios from "axios";
const { Option } = Select;
const { TextArea } = Input;

const BadgeForm = ({
  badgePayload,
  setBadgePayload,
  badges,
  badgeChild,
  modalType,
  parentBadge
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [position, setPosition] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [badgeId, setBadgeId] = useState(null); // Guardar ID para preservar no update

  // Estados para critérios de progresso
  const [criteriaType, setCriteriaType] = useState("none");
  const [selectedVideoTrack, setSelectedVideoTrack] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [criteriaTarget, setCriteriaTarget] = useState(100);
  const [criteriaScope, setCriteriaScope] = useState("track"); // track ou module
  const [videoTracks, setVideoTracks] = useState([]);
  const [modules, setModules] = useState([]);

  // Funções para buscar dados das trilhas e módulos
  const fetchVideoTracks = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GOLANG_API}/api/v2/video-tracks?includeDraft=true|false`
      );
      console.log("API Response for video tracks:", response.data);
      const rawTracks = response.data.data || [];
      // Normalizar: API pode retornar _id ou id
      const normalizedTracks = rawTracks.map(t => ({
        ...t,
        id: t.id || t._id
      }));
      setVideoTracks(normalizedTracks);
    } catch (error) {
      console.error("Error fetching video tracks:", error);
    }
  };

  const fetchModules = async (videoTrackId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GOLANG_API}/api/v2/video-tracks/${videoTrackId}`
      );
      console.log("API Response for video track (full):", response.data.data);
      console.log("Modules in response:", response.data.data?.modules);
      console.log("ModulesEncoded in response:", response.data.data?.modulesEncoded);
      const rawModules = response.data.data?.modules || [];
      // Normalizar: API pode retornar _id ou id
      const normalizedModules = rawModules.map(m => ({
        ...m,
        id: m.id || m._id
      }));
      console.log("Normalized modules:", normalizedModules);
      setModules(normalizedModules);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  // Função para construir criteria
  const buildCriteria = () => {
    if (criteriaType === "none") return [];

    if (criteriaType === "video_completion") {
      const config = {};

      if (criteriaScope === "module" && selectedModule) {
        config.module_id = selectedModule;
      } else if (criteriaScope === "track" && selectedVideoTrack) {
        config.video_track_id = selectedVideoTrack;
      } else {
        return [];
      }

      return [{
        type: "video_completion",
        target: criteriaTarget,
        weight: 1,
        required: true,
        config
      }];
    }

    return [];
  };

  // Buscar video_tracks ao montar componente
  useEffect(() => {
    fetchVideoTracks();
  }, []);

  // Quando trilha selecionada, buscar módulos
  useEffect(() => {
    if (selectedVideoTrack) {
      fetchModules(selectedVideoTrack);
    } else {
      setModules([]);
      setSelectedModule(null);
    }
  }, [selectedVideoTrack]);

  // Inicializar campos apenas quando o modal abrir ou modalType mudar
  useEffect(() => {
    if (modalType === "edit" && badgePayload) {
      setName(badgePayload.name || "");
      setDescription(badgePayload.description || "");
      setIconUrl(badgePayload.icons?.default || badgePayload.icons?.icon16x || "");
      setPosition(badgePayload.position || 0);
      setHidden(badgePayload.hidden || false);
      setParentId(badgePayload.parentId || null);
      setBadgeId(badgePayload.id || null); // Preservar ID para update

      // Carregar critérios existentes
      if (badgePayload.criteria && badgePayload.criteria.length > 0) {
        const firstCriteria = badgePayload.criteria[0];
        if (firstCriteria.type === "video_completion") {
          setCriteriaType("video_completion");
          setCriteriaTarget(firstCriteria.target || 100);

          if (firstCriteria.config?.module_id) {
            setCriteriaScope("module");
            setSelectedModule(firstCriteria.config.module_id);
            // Precisaremos descobrir o video_track_id do módulo para carregar a lista
            // Por enquanto, vamos deixar o usuário reselecionar se necessário
          } else if (firstCriteria.config?.video_track_id) {
            setCriteriaScope("track");
            setSelectedVideoTrack(firstCriteria.config.video_track_id);
          }
        }
      } else {
        // Resetar critérios se não houver
        setCriteriaType("none");
        setCriteriaScope("track");
        setSelectedVideoTrack(null);
        setSelectedModule(null);
        setCriteriaTarget(100);
      }
    }

    if (modalType === "add") {
      setName("");
      setDescription("");
      setIconUrl("");
      setPosition(0);
      setHidden(false);
      setParentId(parentBadge?.id || null);
      setBadgeId(null); // Limpar ID em modo add

      // Resetar critérios
      setCriteriaType("none");
      setCriteriaScope("track");
      setSelectedVideoTrack(null);
      setSelectedModule(null);
      setCriteriaTarget(100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalType, parentBadge?.id]); // badgePayload removido para evitar loop - usamos key={modalKey} no parent

  // Atualizar o payload do pai quando os campos locais mudarem
  useEffect(() => {
    const payload = {
      name,
      description,
      icons: {
        default: iconUrl,
        locked: "",
      },
      position: parseInt(position) || 0,
      hidden,
      parentId,
      criteria: buildCriteria(),
    };

    // Preservar ID em modo edit
    if (badgeId) {
      payload.id = badgeId;
    }

    setBadgePayload(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, description, iconUrl, position, hidden, parentId, badgeId, criteriaType, selectedVideoTrack, selectedModule, criteriaTarget, criteriaScope, setBadgePayload]);

  const handleBadgeSelect = (value) => {
    const selected = badges.find((b) => b.id === value);
    if (selected) {
      setName(selected.name || "");
      setDescription(selected.description || "");
      setIconUrl(selected.icons?.default || selected.icons?.icon16x || "");
      // Manter position e hidden do form
    }
  };

  return (
    <div>
      {modalType === "add" && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
              Select Existing Badge (optional)
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select a badge to copy data from"
              allowClear
              onChange={handleBadgeSelect}
            >
              {badges.map((b) => (
                <Option key={b.id} value={b.id}>
                  <Space>
                    {b.icons?.default || b.icons?.icon16x ? (
                      <img
                        src={b.icons?.default || b.icons?.icon16x}
                        alt={b.name}
                        style={{ width: 16, height: 16 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : null}
                    {b.name}
                  </Space>
                </Option>
              ))}
            </Select>
          </div>
          <Divider />
        </>
      )}

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
          Name *
        </label>
        <Input
          placeholder="Badge Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
          Description
        </label>
        <TextArea
          placeholder="Badge Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
          Icon URL
        </label>
        <Input
          placeholder="https://example.com/badge-icon.png"
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
        />
        {iconUrl && (
          <div style={{ marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#888" }}>Preview: </span>
            <img
              src={iconUrl}
              alt="icon preview"
              style={{ width: 32, height: 32, marginLeft: 8 }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}
      </div>

      {modalType === "add" && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
            Parent Badge
          </label>
          {parentBadge ? (
            <div style={{
              padding: 8,
              background: "#f0f0f0",
              borderRadius: 4,
              marginBottom: 8
            }}>
              <Space>
                {parentBadge.icons?.default && (
                  <img
                    src={parentBadge.icons.default}
                    alt={parentBadge.name}
                    style={{ width: 16, height: 16 }}
                  />
                )}
                <span>{parentBadge.name}</span>
              </Space>
            </div>
          ) : (
            <Select
              style={{ width: "100%" }}
              placeholder="Select parent badge (optional)"
              allowClear
              value={parentId}
              onChange={(value) => setParentId(value || null)}
            >
              {badges.map((b) => (
                <Option key={b.id} value={b.id}>
                  <Space>
                    {b.icons?.default || b.icons?.icon16x ? (
                      <img
                        src={b.icons?.default || b.icons?.icon16x}
                        alt={b.name}
                        style={{ width: 16, height: 16 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : null}
                    {b.name}
                  </Space>
                </Option>
              ))}
            </Select>
          )}
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            Leave empty to create a root badge
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
          Position
        </label>
        <Input
          type="number"
          placeholder="0"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Order in which this badge appears (0 = first)
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
          Hidden
        </label>
        <Select
          value={hidden}
          onChange={(value) => setHidden(value)}
          style={{ width: "100%" }}
        >
          <Option value={false}>Visible (Normal)</Option>
          <Option value={true}>Hidden (Secret Badge)</Option>
        </Select>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Hidden badges are only shown to users who unlocked them
        </div>
      </div>

      <Divider style={{ margin: "24px 0" }} />

      {/* Seção de Critérios de Progresso */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 8, fontWeight: "bold", fontSize: 16 }}>
          Critério de Progresso
        </label>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
            Tipo
          </label>
          <Select
            value={criteriaType}
            onChange={(value) => {
              setCriteriaType(value);
              if (value === "none") {
                setSelectedVideoTrack(null);
                setSelectedModule(null);
                setCriteriaScope("track");
              }
            }}
            style={{ width: "100%" }}
          >
            <Option value="none">Nenhum (Badge manual)</Option>
            <Option value="video_completion">Conclusão de Vídeos</Option>
          </Select>
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            Define como o usuário pode desbloquear este badge
          </div>
        </div>

        {criteriaType === "video_completion" && (
          <div style={{
            padding: 16,
            background: "#f5f5f5",
            borderRadius: 8,
            border: "1px solid #e0e0e0"
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
                Trilha de Vídeos
              </label>
              <Select
                value={selectedVideoTrack}
                onChange={(value) => {
                  setSelectedVideoTrack(value);
                  setSelectedModule(null);
                }}
                style={{ width: "100%" }}
                placeholder="Selecione uma trilha..."
                showSearch
                optionFilterProp="children"
              >
                {videoTracks.map((track) => (
                  <Option key={track.id} value={track.id}>
                    {track.name}
                  </Option>
                ))}
              </Select>
            </div>

            {selectedVideoTrack && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
                    Escopo
                  </label>
                  <Radio.Group
                    value={criteriaScope}
                    onChange={(e) => {
                      setCriteriaScope(e.target.value);
                      if (e.target.value === "track") {
                        setSelectedModule(null);
                      }
                    }}
                  >
                    <Space direction="vertical">
                      <Radio value="track">Trilha Completa</Radio>
                      <Radio value="module">Módulo Específico</Radio>
                    </Space>
                  </Radio.Group>
                </div>

                {criteriaScope === "module" && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
                      Módulo
                    </label>
                    <Select
                      value={selectedModule}
                      onChange={(value) => setSelectedModule(value)}
                      style={{ width: "100%" }}
                      placeholder="Selecione um módulo..."
                      showSearch
                      optionFilterProp="children"
                      disabled={modules.length === 0}
                    >
                      {modules.map((module) => (
                        <Option key={module.id} value={module.id}>
                          {module.name}
                        </Option>
                      ))}
                    </Select>
                    {modules.length === 0 && (
                      <div style={{ fontSize: 12, color: "#ff6b6b", marginTop: 4 }}>
                        Nenhum módulo encontrado para esta trilha
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: 0 }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
                    Meta de Conclusão (%)
                  </label>
                  <InputNumber
                    value={criteriaTarget}
                    onChange={(value) => setCriteriaTarget(value || 100)}
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                  />
                  <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                    Percentual de vídeos que devem ser concluídos (1-100%)
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeForm;
